import { Text, Title2, WasabiButton } from '@kk/ui';

import { useClients } from '@/auth/SessionProvider';
import { GetTheApp } from '@/components/GetTheApp';
import { deck } from '@/copy/deck';
import { SignInShell } from '@/screens/SignInShell';

/**
 * **"This account isn't on a club"** — the ruled refusal (COPY_DECK, *Portal states*, 2026-09-16) for a person
 * Auth0 says yes to and this API says no to. Almost always a parent.
 *
 * **It lives here rather than inside `SignInScreen` because TWO paths reach it**, and until 2026-09-23 the
 * second one was a redirect loop. A sign-in refused lands here through `problem === 'notStaff'`; and somebody
 * already signed in whose account holds no membership lands here from the club portal's own landing, which
 * used to send them to `/approvals` → `RequireSession` → `/sign-in` → back again, forever. A refusal with a way
 * out is the only thing that terminates — the same reasoning `RequireSuperAdmin` records for the admin host.
 *
 * The way to the app is `GetTheApp`, never a link: this card once sent a desktop reader to
 * `app.kiddiekredit.com`, which answers with raw JSON.
 *
 * **"Sign in with a different account" signs OUT**, which is the only way off this screen and the reason it is
 * a real sign-out rather than a navigation — the session that is refused has to end, or the next sign-in is the
 * same one.
 */
export function NotOnAClub() {
  const { auth } = useClients();
  return (
    <SignInShell>
      <div className="stack" data-testid="not-staff">
        <Title2 tone="ink">{deck.states.notStaff.title}</Title2>
        <Text variant="bodySm" tone="slate">
          {deck.states.notStaff.body}
        </Text>
        <Text variant="bodySm" tone="slate" testID="how-to-get-access">
          {deck.states.notStaff.howToGetAccess}
        </Text>
        <GetTheApp testID="not-staff-app" />
        <WasabiButton
          label={deck.states.notStaff.other}
          variant="text"
          textSize="sm"
          fullWidth={false}
          onPress={() => void auth.signOut()}
          testID="state-link"
        />
      </div>
    </SignInShell>
  );
}
